import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { convertObjectToArray, getRemotedbUrl, i18nIntl, isEmpty, isNotEmpty, MD5, stringifyArrayOfItems } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';

import FloatingLayerPublic from '../front/FloatingLayerPublic.js';
import PrizeImage from '../front/PrizeImage';
import LeveledHTMLText from '../front/helpers/LeveledHTMLText';

import ActionLink from '../builder/ActionLink';
import TimeCounterSimple from '../builder/TimeCounterSimple';
import CopyLink from '../builder/CopyLink.js';

import HistoricLogsMap from '../utils/HistoricLogsMap.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import ActionButton from '../builder/ActionButton.js';

const UNLIMITED_PRIZE = 'unlimited';

const Wrapper = ({ children, ...props }) => {
	const in_layer = props?.in_layer ?? true;
	const goBackLink = props?.goBackLink ?? true;

	const classes = ['prize_winner_content', 'this_contents', 'gui_pos_rel'];
	if(props?.in_validation ?? false) classes.push('in_validation');
	if(props?.is_multiprize ?? false) classes.push('is_multiprize');

	if(!in_layer) {
		return (
			<div class={classes.join(" ")}>
				{(isNotEmpty(props, 'title')) && (
					<LeveledHTMLText>{props.title}</LeveledHTMLText>
				)}

				{children}
			</div>
		)
	}

	return (
		<FloatingLayerPublic
			{...props}
		>
			<div class={classes.join(" ")}>
				{children}

				{(goBackLink !== false) && (
					<div class="go_back gui_not_in_top">{goBackLink}</div>
				)}
			</div>
		</FloatingLayerPublic>
	)
};

export const WinnerPrizeIndex = (props) => {
	useCSS('comp/winner_content.css');

	const is_multiprize = (props.prizes.length > 1) ? true : false;

	const [ current_prize, setCurrentPrize ] = useState((is_multiprize) ? -1 : 0);

	if(is_multiprize && current_prize == -1) {
		return (
			<Wrapper
				pm={{}}
				is_builder={props.is_builder}
				backButton={props.backButton}
			>
				<LeveledHTMLText>
					<h1>
						{props.intl.formatMessage({
							id:"You've won {prizes} prizes",
						}, {
							prizes:props.prizes.length,
						})}
					</h1>
				</LeveledHTMLText>

				<div class="prize_ctas gui_not_in_top_big">
					{props.prizes.map((prize, p) => {
						return (
							<ActionLink
								key={`prize_${prize.Prize.id}`}
								onClick={() => setCurrentPrize(p)}
								_class={{ other:`single_prize is_${prize.Prize.id}` }}
							>{prize.PrizeType.title}</ActionLink>
						)
					})}
				</div>
			</Wrapper>
		)
	}

	return (
		<WinnerContent
			{...props}
			prize={props.prizes[current_prize].PrizeType}
			code={props.prizes[current_prize].PromoCode}
			code_validation={props.prizes[current_prize]?.CodeValidation ?? null}
			promo_claim={props.prizes[current_prize]?.PromoClaim ?? null}
			share={props.prizes[current_prize].Share}
			user={props.prizes[current_prize].User}
			user_prize={props.prizes[current_prize].Prize}
			is_multiprize={is_multiprize}
			goBackLink={(is_multiprize) ? <ActionLink
				onClick={() => setCurrentPrize(-1)}
			>Go back</ActionLink> : null}
		/>
	)
};

export const SimpleWinnerContent = (props) => {
	useCSS('comp/winner_content.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const just_claimed = props?.just_claimed ?? false;

	const _prize = props?.prize ?? {};
	const prize = _prize?.PrizeType ?? {};
	const user_prize = _prize?.Prize ?? {};
	const video = prize?.video_winner ?? false;
	const video_thumb = prize?.video_winner_thumb ?? (prize?.video_thumb ?? null);
	const autoplay = ((props?.autoplay ?? false) && video_ap) ? true : false;

	let image = false;
	if(!video) {
		if(isNotEmpty(prize, 'full_image')) image = prize.full_image;
		if(isNotEmpty(prize, 'full_image_winner')) image = prize.full_image_winner;
	}

	let description = false;
	if(isNotEmpty(prize, 'description')) description = prize.description;
	if(isNotEmpty(prize, 'winner_description')) description = prize.winner_description;

	const extra_info = (isNotEmpty(prize, 'info')) ? true : false;
	const prize_instructions = (isNotEmpty(prize, 'instructions')) ? true : false;

	const available_status_props = {
		pending:{ id:0, front_title:"Pending to claim", title:"Pending", color:'midgray' },
		claimed:{ id:1, title:"Claimed", color:'yellow' },
		progress:{ id:2, title:"In progress", color:'orange', is_light:false },
		delivered:{ id:3, title:"Delivered", color:'green', is_light:false },
		cancelled:{ id:4, title:"Cancelled", color:'red', is_light:false },
		expired:{ id:0, title:"Expired", color:'red', is_light:false },
	};

	const downloadable_type = (isNotEmpty(prize, 'downloadable', ['0'])) ? prize.downloadable : false;
	const is_downloadable = (downloadable_type !== false) ? true : false;
	let download_url = false;
	if(is_downloadable && prize.id != undefined) {
		download_url = (user_prize.id != undefined) ? `/prize_coupon/${user_prize.id}/${user_prize.hash}` : `/prizes/prize_export/${prize.id}`;
	}

	const code = (prize.unique_code != undefined) ? prize.unique_code : (_prize?.PromoCode?.code ?? false);

	return (
		<div class="simple_prize_content">
			{(just_claimed === false || true) && (
				<div class="prize_detail_preview">
					<LeveledHTMLText
						_class={{ other:'prize_name in_bottom' }}
					>
						<h2>{prize.title}</h2>
					</LeveledHTMLText>

					{(image !== false || video !== false) && (
						<PrizeImage
							prize={prize}
							image={image}
							video={video}
							video_thumb={video_thumb}
							video_props={{
								page:`prize_${prize.id}`,
								promotion_id:prize.promotion_id,
								autoplay,
							}}
						/>
					)}

					{(description !== false) && (
						<LeveledHTMLText
							_class={{ other:'short_description in_bottom' }}
						>
							<p dangerouslySetInnerHTML={{ __html:`${description.replace(/\n/g, '<br />')}` }} />
						</LeveledHTMLText>
					)}

					{(extra_info) && (
						<div class="prize_extra_info">
							<h4 class="group_title">{i18n("Prize details")}</h4>

							<LeveledHTMLText
								_class={{ other:'gui_text_align_start' }}
							>{prize.info}</LeveledHTMLText>
						</div>
					)}
				</div>
			)}

			{(download_url !== false) && (
				<ButtonCollection
					_class={{ other:'flex_button gui_big_button' }}
				>
					<ActionButton
						icon='knk-download'
						_fullclass='prize_download in_top'
						_href={getRemotedbUrl(download_url)}
					>Download</ActionButton>
				</ButtonCollection>
			)}

			{/* if the prize download comes from a fileset (downloadable_type == '3') do not display the copy link
			because it's going to be the filename to be download and not a specific code */}
			{(code !== false && downloadable_type != '3') && (
				<div class="code won_code">
					<p style={{ fontSize:'2em', marginBlockEnd:'0.3rem' }}>
						<strong>{code}</strong>
					</p>

					<CopyLink
						copy_link={code}
						autotranslate={true}
					>Copy code</CopyLink>
				</div>
			)}

			{(prize_instructions) && (
				<div
					class="prize_instructions"
					style={{
						backgroundColor:'rgba(var(--text-color-rgb), 0.05)',
						padding:'0.8em 1em',
						borderRadius:'0.5em',
					}}
				>
					<h4 class="group_title">{i18n("Instructions")}:</h4>

					<LeveledHTMLText
						_class={{ other:'gui_text_align_start' }}
					>{prize.instructions}</LeveledHTMLText>
				</div>
			)}

			{(just_claimed === false && isNotEmpty(props, 'status_log')) && (
				<HistoricLogsMap
					current={props.status_log}
					all_steps={convertObjectToArray(available_status_props)}
					start_steps={['pending']}
					end_steps={['delivered', 'cancelled']}
					is_backend={false}
					//date_format='ddd L, LT'
				/>
			)}
		</div>
	)
};

const WinnerContent = (props) => {
	useCSS('comp/winner_content.css');

	const seconds_to_redeem = props?.user_prize?.seconds_to_redeem ?? UNLIMITED_PRIZE;
	const seconds_to_claim = props?.user_prize?.seconds_to_claim ?? UNLIMITED_PRIZE;
	const history = useHistory();

	const [ expirated, setExpirated ] = useState((seconds_to_redeem < 1 && seconds_to_redeem != UNLIMITED_PRIZE) ? true : false);
	const [ claim_expirated, setClaimExpirated ] = useState((seconds_to_claim < 1 && seconds_to_claim != UNLIMITED_PRIZE) ? true : false);

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_builder = props?.is_builder ?? false;

	const prize = props?.prize ?? {};
	const user = props?.user ?? {};
	const user_prize = props?.user_prize ?? { id:'123', qr_code:567 };
	const code_validation = props?.code_validation ?? null;
	const promo_claim = props?.promo_claim ?? null;

	const video = prize?.video_winner ?? false;
	const video_thumb = prize?.video_winner_thumb ?? (prize?.video_thumb ?? null);
	const video_ap = prize?.video_winner_ap ?? (prize?.video_ap ?? false);
	const autoplay = ((props?.autoplay ?? false) && video_ap) ? true : false;

	let image = false;
	if(!video) {
		if(isNotEmpty(prize, 'full_image')) image = prize.full_image;
		if(isNotEmpty(prize, 'full_image_winner')) image = prize.full_image_winner;
	}

	let description = false;
	if(isNotEmpty(prize, 'description')) description = prize.description;
	if(isNotEmpty(prize, 'winner_description')) description = prize.winner_description;

	const prize_instructions = (isNotEmpty(prize, 'instructions')) ? true : false;
	const extra_info = (isNotEmpty(prize, 'info')) ? true : false;
	const is_downloadable = (isNotEmpty(prize, 'downloadable', ['0'])) ? true : false;
	const is_file_set = prize.downloadable == '3';
	const code = (prize.unique_code != undefined) ? { code:prize.unique_code } : (props.code ? props.code : { id:1, code:'example' }) ;

	let download_url = false;
	if(is_downloadable && prize != undefined && user != undefined) {
		download_url = (user_prize.id != undefined) ? `/prize_coupon/${user_prize.id}/${user_prize.hash}` : `/prizes/prize_export/${prize.id}`;
	}

	const data_share = {
		promotion_id:prize.promotion_id,
		type:'prize',
	};

	if(user != undefined) {
		data_share.user_id = user.id;
		data_share.page = 'winner';
	}

	const winner_text_placeholder = `<h1>${i18n("Congratulations!")}</h1><p>${i18n("This is your prize")}: <strong>${prize.title}</strong></p>`;

	const time_counter_seconds = (!is_builder) ? seconds_to_redeem : parseInt(prize?.prize_expiration_time ?? UNLIMITED_PRIZE);
	const is_redeemable = prize.redeemable ? true : false;
	const in_layer = prize?.is_layer ?? (props?.in_layer ?? true);
	const is_redeemed = code_validation?.id ?? false;
	
	if(props?.in_validation ?? false) {
		let prize_title = (isNotEmpty(prize, 'title', [" "])) ? `<h1>${prize.title}</h1>` : '';
		if(description !== false) prize_title += `<p>${description.replace(/\n/g, '<br />')}</p>`;

		return (
			<Wrapper
				in_layer={false}
				title={prize_title}
				in_validation={true}
			>
				<PrizeImage
					prize={prize}
					image={image}
					video={video}
					video_thumb={video_thumb}
					video_props={{
						autoplay,
					}}
				/>
			</Wrapper>
		)
	}
	let time_format = `{m}${i18n("m")} {s}${i18n("s")}`;
	if(time_counter_seconds > 0) {
		if(time_counter_seconds >= 60 * 60) time_format = `{h}${i18n("h")} {m}${i18n("m")} {s}${i18n("s")}`;
		if(time_counter_seconds > 24 * 60 * 60) time_format = `<span class="days">{d2}&nbsp;${i18n("day")}</span> {h}${i18n("h")}`;
		if(time_counter_seconds > 48 * 60 * 60) time_format = `<span class="days">{d2}&nbsp;${i18n("days")}</span> {h}${i18n("h")}`;
	}

	const has_code = !is_file_set && ((prize.code_set && code.id > 0) || prize.unique_code != undefined);
	const prize_is_redeemed = (is_redeemable && is_redeemed) ? true : false;
	const prize_is_expired = (is_redeemable && expirated && !is_redeemed) ? true : false;
	const prize_is_redeemable = (is_redeemable && !expirated && !is_redeemed) ? true : false;
	const prize_is_downloadable = (download_url !== false && (!is_redeemable || !expirated)) ? true : false;
	const file_set_and_has_code = (!is_file_set || (is_file_set && !isEmpty(props?.code,'id'))) ? true : false;
	
	const isPrizeRejectable = () => {
		if(isNotEmpty(prize, 'rejectable', ["", " ", "0"])) return true;
		return false;
	};

	const isPrizeClaimable = () => {
		if(isNotEmpty(prize, 'claimable', ["", " ", "0"])) return true;
		return false;
	};

	//FOR CLAIM
	const claim_counter_seconds = (!is_builder) ? seconds_to_claim : parseInt(prize?.claim_expiration_time ?? UNLIMITED_PRIZE);
	const is_claimable = isPrizeClaimable();
	let claim_time_format = `{m}${i18n("m")} {s}${i18n("s")}`;
	if(claim_counter_seconds > 0) {
		if(claim_counter_seconds >= 60 * 60) claim_time_format = `{h}${i18n("h")} {m}${i18n("m")} {s}${i18n("s")}`;
		if(claim_counter_seconds > 24 * 60 * 60) claim_time_format = `<span class="days">{d2}&nbsp;${i18n("day")}</span> {h}${i18n("h")}`;
		if(claim_counter_seconds > 48 * 60 * 60) claim_time_format = `<span class="days">{d2}&nbsp;${i18n("days")}</span> {h}${i18n("h")}`;
	}

	let claim_url = false;
	let route_navigation = false;
	let full_claim_url =  `/my_prizes/${prize.promotion_id}`;
	if(is_claimable) {
		if(user.stage == 0) {
			claim_url = `/users/claim/${prize.promotion_id}`;
			claim_url += `/?ct=${MD5(`claim_${prize.promotion_id}_${user.login_id}`)}&lid=${props.user.login_id}`;
		} else if(user_prize.id != undefined) {
			claim_url = `${user_prize.id}`;
			full_claim_url += `/${user_prize.id}`;
			route_navigation = true;
		} else {
			claim_url = `/my_prizes/${prize.promotion_id}`;
		}
	}
	
	const is_rejectable = isPrizeRejectable();
	const reject_url = `${claim_url}/reject`;
	const full_reject_url = `${full_claim_url}/reject`;

	const is_claimed = promo_claim?.id ?? false;
	const prize_is_claimed = (is_claimable && is_claimed) ? true : false;
	const prize_is_claimable = (is_claimable && !claim_expirated && !is_claimed) ? true : false;
	const prize_is_claim_expired = (is_claimable && claim_expirated && !is_claimed) ? true : false;
	const hide_prize_info = (is_claimable && !is_claimed) ? true : false;

	const has_any_cta = (prize_is_redeemed || 
						prize_is_expired || 
						prize_is_redeemable || 
						prize_is_downloadable || 
						prize_is_claimed || 
						prize_is_claim_expired || 
						prize_is_claimable ||
						is_rejectable) ? true : false;

	const enabled_pos = prize?.prize_pos ?? false;
	const prize_pos_names = prize?.prize_pos_names ?? [];

	const pos_names_str = stringifyArrayOfItems(prize_pos_names, i18n(" and "));

	return (
		<Wrapper
			in_layer={in_layer}
			pm={{}}
			is_builder={props.is_builder}
			title={(isNotEmpty(prize, 'winner_text')) ? prize.winner_text.replace(/\n/g, '<br />') : winner_text_placeholder}
			autotranslate={false}
			in_validation={false}
			backButton={props.backButton}
			is_multiprize={props.is_multiprize}
			footer_buttons={props.footer_buttons}
			goBackLink={props.goBackLink}
		>
			<div class="prize_content">
				<div class="flex_contents">
					<div class="gui_flexbox gui_flex_col gui_flex_nowrap gui_full_width gui_margined_children_v">
						<PrizeImage
							prize={prize}
							image={image}
							video={video}
							video_thumb={video_thumb}
							video_props={{
								page:(!is_builder) ? `prize_${prize.id}` : null,
								promotion_id:(!is_builder) ? prize.promotion_id : null,
								autoplay,
							}}
						/>

						{(description !== false) && (
							<LeveledHTMLText
								_class={{ other:'short_description' }}
							>
								<p dangerouslySetInnerHTML={{ __html:`${description.replace(/\n/g, '<br />')}` }} />
							</LeveledHTMLText>
						)}

						{(has_code && !hide_prize_info) && (
							<div class="code won_code">
								<span>{i18n("Code")}:</span> <strong>{code.code}</strong> <CopyLink copy_link={code.code}>Copy</CopyLink>
							</div>
						)}

						{(has_any_cta) && (
							<div class="prize_ctas">
								{(prize_is_redeemed) && (
									<div class="prize_redeem is_redeemed">
										<p class="knk-on greenIcon gui_padded_icon gui_small_icon">{i18n("***redeemed", 'ucfirst')}</p>
									</div>
								)}

								{(prize_is_expired || prize_is_claim_expired) && (
									<div class="prize_redeem is_redeemed">
										<p class="knk-off redIcon gui_padded_icon gui_small_icon">{i18n("Expired")}</p>
									</div>
								)}

								{(prize_is_redeemable) && (
									<ActionLink
										_href={(!is_builder) ? getRemotedbUrl(`/redeem/${user_prize.id}/${user_prize.hash}`) : null}
										_class={{ other:'prize_redeem' }}
										autotranslate={false}
									>
										{i18n("***Redeem")}

										{(time_counter_seconds > 0) && (
											<TimeCounterSimple
												seconds={time_counter_seconds}
												hide_days={false}
												hide_hours={false}
												execute_countdown={(!is_builder) ? true : false}
												custom_render={`${i18n("Time left")}: <strong class="gui_not_in_start_tiny">${time_format}</strong>`}
												onFinish={() => {
													setExpirated(true);
												}}
											/>
										)}
									</ActionLink>
								)}

								{(prize_is_downloadable && file_set_and_has_code && !hide_prize_info) && (
									<ActionLink
										_href={(!is_builder) ? getRemotedbUrl(download_url) : null}
										//_target="_blank"	// this target='_blank' doesn't work in in-app browsers
										_class={{ other:'prize_download' }}
									>Download</ActionLink>
								)}

								{(prize_is_claimable && is_builder) && (
									<ActionLink
										_class={{ other:'prize_claim' }}
									>Claim your prize here</ActionLink>
								)}

								{(prize_is_claimable && !is_builder) && (
									<ActionLink
										_href={(route_navigation === false) ? getRemotedbUrl(claim_url) : null}
										_target='_blank'
										onClick={(route_navigation !== false) ? () => {
											if(history) {
												if(props.backButton != undefined) props.backButton();
												history.push(claim_url);
											} else {
												window.location.href = full_claim_url;
											}
										} : null}
										_class={{ other:'prize_claim' }}
										autotranslate={false}
									>
										{i18n("Claim your prize here")}

										{(claim_counter_seconds > 0) && (
											<TimeCounterSimple
												seconds={claim_counter_seconds}
												hide_days={false}
												hide_hours={false}
												execute_countdown={(!is_builder) ? true : false}
												custom_render={`${i18n("Time left")}: <strong class="gui_not_in_start_tiny">${claim_time_format}</strong>`}
												onFinish={() => {
													setClaimExpirated(true);
												}}
											/>
										)}
									</ActionLink>
								)}
							</div>
						)}

						{(prize_is_claimable && is_rejectable) && (
							<div>
								<ActionLink
									_href={(route_navigation === false) ? getRemotedbUrl(reject_url) : null}
									_target='_blank'
									onClick={(route_navigation !== false) ? () => {
										if(history) {
											if(props.backButton != undefined) props.backButton();
											history.push(reject_url);
										} else {
											window.location.href = full_reject_url;
										}
									} : null}
								>I don't want it</ActionLink>
							</div>
						)}

						{(extra_info) && (
							<div class="prize_extra_info">
								<h4 class="group_title">{i18n("Prize details")}</h4>

								<LeveledHTMLText
									_class={{ other:'gui_text_align_start' }}
								>{prize.info}</LeveledHTMLText>
							</div>
						)}

						{(prize_instructions && !hide_prize_info) && (
							<div class="prize_instructions">
								<h4 class="group_title">{i18n("Instructions")}</h4>

								<LeveledHTMLText
									_class={{ other:'gui_text_align_start' }}
								>{prize.instructions}</LeveledHTMLText>
							</div>
						)}

						{(enabled_pos !== false) && (
							<div class="prize_valid_in">
								<h4 class="group_title">{i18n("Valid in")}</h4>

								<LeveledHTMLText
									_class={{ other:'gui_text_align_start' }}
								>{pos_names_str}</LeveledHTMLText>
							</div>
						)}
					</div>
				</div>
			</div>
		</Wrapper>
	);
}

export default injectIntl(WinnerContent);